import { attendanceCellStatus, attendanceCellSymbol, type AttendanceCellInput, type AttendanceCellStatus } from './attendance';
import type { DayMarkKind, DayMarkStatus } from './day-marks';
import { driverPayItemCalc, type DriverPayType } from './driver-attendance';

/**
 * DEC-950 – Phiếu lương kèm bảng công (anh Tiến 04/11/2026: "kết hợp bảng công và lương. Làm Phiếu Lương cho từng nhân viên và gởi
 * tài khoản của họ, ở mục Lương của tôi. Ngày công hiển thị giống bảng công. Thêm: Tăng ca, Về trễ, Hỗ trợ, Qua đêm. Riêng mục Công:
 * ngày công, ngày phép, KL, V. Đánh dấu để nhận biết" + "thêm mục Phép năm: +1 hoặc 0").
 *
 * Lúc Tính lương, mỗi dòng lương chụp lại "tờ công" của NV (lịch ngày, đếm P/KL/V, 4 khoản Tăng ca / Về trễ / Hỗ trợ / Qua đêm
 * kèm ghi chú từng ngày, phép năm tháng đó +1 hay 0). Duyệt bảng lương = khóa luôn tờ công; NV xem đúng số đã trả.
 */

/** Một ô lịch trên phiếu lương (đã tính sẵn trạng thái + ký hiệu như "Bảng công của tôi"). */
export interface PayslipDay {
  /** YYYY-MM-DD */
  d: string;
  st: AttendanceCellStatus;
  /** Ký hiệu ô: 1 · 0,5 · P · KL · V · Trễ · LCN… ('' = để trống) */
  sym: string;
  /** Giờ tăng ca của ngày (0 = không) */
  ot: number;
  /** Trạng thái ngày đã duyệt: Đ Xuyên đêm · T Về trễ · H Hỗ trợ · S Đi sớm */
  marks: DayMarkKind[];
  /** Có tiền Về trễ / Qua đêm / Hỗ trợ / Tăng ca tài xế trong ngày (để tô viền) */
  paid?: boolean;
}

export const PAYSLIP_EXTRA_KEYS = ['ot', 'lateReturn', 'support', 'overnight'] as const;
export type PayslipExtraKey = (typeof PAYSLIP_EXTRA_KEYS)[number];
export const PAYSLIP_EXTRA_LABELS: Record<PayslipExtraKey, string> = {
  ot: 'Tăng ca',
  lateReturn: 'Về trễ',
  support: 'Hỗ trợ',
  overnight: 'Qua đêm',
};

export interface PayslipExtra {
  /** Số lượng: giờ (unit 'h') hoặc ngày (unit 'ngày') */
  qty: number;
  unit: 'h' | 'ngày';
  /** Tiền tính từ bảng tiền tài xế (NV văn phòng: null – lấy theo khoản lương tăng ca) */
  amount: number | null;
  /** Ghi chú từng ngày, VD "20/10 Hỗ trợ đêm (về bãi 23:08): 150.000" */
  notes: string[];
}

export type PayslipLeaveReason = 'not_official' | 'low_work' | 'absent' | 'unexcused';
export const PAYSLIP_LEAVE_REASON_LABELS: Record<PayslipLeaveReason, string> = {
  not_official: 'chưa là NV chính thức',
  low_work: 'chưa đủ công tối thiểu',
  absent: 'nghỉ nhiều ngày trong tháng',
  unexcused: 'vắng không phép',
};

export interface PayslipLeave {
  /** Phép năm cộng tháng này: +1 hoặc 0 */
  accrued: number;
  reason: PayslipLeaveReason | null;
  /** Diễn giải lý do (VD "công 20 < 24") */
  detail: string | null;
  /** Số phép còn cuối tháng */
  closing: number | null;
}

export interface PayslipSheet {
  v: 1;
  month: string;
  std: number;
  work: number;
  /** Ngày phép có lương / không lương / vắng / làm CN / số lần trễ */
  p: number;
  kl: number;
  absent: number;
  lcn: number;
  late: number;
  days: PayslipDay[];
  extras: Record<PayslipExtraKey, PayslipExtra>;
  leave: PayslipLeave | null;
}

export type PayslipCellInput = AttendanceCellInput & {
  otHours: number;
  marks?: readonly { kind: DayMarkKind; status: DayMarkStatus; place: string | null }[] | null;
};

export interface PayslipDriverDay {
  date: string;
  items: readonly { type: DriverPayType; label: string; amount: number; minutes?: number; rate?: number; note?: string }[];
  /** Ngày đã duyệt Xuyên đêm / Về trễ mà không có tiền đêm: lý do (chuyến tỉnh, chuyến lấy hàng, chưa có giờ về bãi…) */
  nightNote?: string;
  /** Ngày đã duyệt Hỗ trợ mà không có tiền xuyên trưa: lý do (km GPS trưa…) */
  supportNote?: string;
}

/** Ô "Bảng công của tôi": ngày trễ / về sớm ghi "Trễ"; ngày chưa tới / đang làm / nghỉ tuần không công để trống. */
export function myAttendanceCellText(c: AttendanceCellInput, date: string, today: string): string {
  const st = attendanceCellStatus(c, date, today);
  if (st === 'late') return 'Trễ';
  if (st === 'future' || st === 'working') return '';
  if (st === 'off' && c.work <= 0) return '';
  return attendanceCellSymbol(st, c.work, date, c.leave);
}

const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const vnd = (n: number) => n.toLocaleString('vi-VN');
const r2 = (n: number) => Math.round(n * 100) / 100;

const OT_TYPES = new Set<DriverPayType>(['early', 'after17']);
const NIGHT_TYPES = new Set<DriverPayType>(['night', 'night_assistant', 'after_midnight']);
const SUPPORT_TYPES = new Set<DriverPayType>(['lunch']);

export function buildPayslipSheet(i: {
  month: string;
  std: number;
  today: string;
  days: readonly string[];
  cells: Record<string, PayslipCellInput | undefined>;
  totals: { work: number; leavePaid?: number; leaveUnpaid?: number; lateCount: number };
  /** Tài xế / phụ xe: tiền từng ngày (null = không phải tài xế) */
  driverDays?: readonly PayslipDriverDay[] | null;
  driverTotals?: { pickup: number; trip: number } | null;
  leave?: PayslipLeave | null;
}): PayslipSheet {
  const ex = (unit: 'h' | 'ngày'): PayslipExtra => ({ qty: 0, unit, amount: null, notes: [] });
  const isDriver = !!i.driverDays;
  const extras: Record<PayslipExtraKey, PayslipExtra> = {
    ot: ex(isDriver ? 'ngày' : 'h'),
    lateReturn: ex('ngày'),
    support: ex('ngày'),
    overnight: ex('ngày'),
  };
  const dayset: Record<PayslipExtraKey, Set<string>> = { ot: new Set(), lateReturn: new Set(), support: new Set(), overnight: new Set() };
  const add = (k: PayslipExtraKey, d: string | null, amount: number, note: string) => {
    const e = extras[k];
    if (d) dayset[k].add(d);
    e.amount = (e.amount ?? 0) + amount;
    e.notes.push(note);
  };
  const approved = (d: string) => (i.cells[d]?.marks ?? []).filter((m) => m.status === 'approved');
  const paidDays = new Set<string>();
  const nightNoteOf = new Map((i.driverDays ?? []).filter((x) => x.nightNote).map((x) => [x.date, x.nightNote!]));
  const supportNoteOf = new Map((i.driverDays ?? []).filter((x) => x.supportNote).map((x) => [x.date, x.supportNote!]));

  // Tài xế: tiền từng ngày theo bảng tiền tài xế
  for (const dd of i.driverDays ?? []) {
    const overnight = approved(dd.date).some((m) => m.kind === 'overnight');
    for (const it of dd.items) {
      if (!it.amount || it.amount < 0) continue;
      const k: PayslipExtraKey | null = OT_TYPES.has(it.type)
        ? 'ot'
        : NIGHT_TYPES.has(it.type)
          ? overnight
            ? 'overnight'
            : 'lateReturn'
          : SUPPORT_TYPES.has(it.type)
            ? 'support'
            : null;
      if (!k) continue;
      const calc = driverPayItemCalc(it);
      add(k, dd.date, it.amount, `${ddmm(dd.date)} ${it.label}${it.note ? ` (${it.note})` : ''}${calc ? ` ${calc}` : ''}: ${vnd(it.amount)}`);
      paidDays.add(dd.date);
    }
  }
  if (i.driverTotals?.pickup) add('support', null, i.driverTotals.pickup, `Chuyến lấy hàng đã duyệt: ${vnd(i.driverTotals.pickup)}`);
  if (i.driverTotals?.trip) add('overnight', null, i.driverTotals.trip, `Công tác phí, lưu trú chuyến tỉnh: ${vnd(i.driverTotals.trip)}`);

  const days: PayslipDay[] = [];
  let p = 0;
  let kl = 0;
  let absent = 0;
  let lcn = 0;
  let otHours = 0;
  for (const d of i.days) {
    const c = i.cells[d];
    if (!c) continue;
    const st = attendanceCellStatus(c, d, i.today);
    if (c.leave && !c.offDay) {
      if (c.leave.paid) p += c.leave.fraction;
      else kl += c.leave.fraction;
    }
    if (c.offDay && (c.sundayWork || c.work > 0)) lcn += 1;
    if (st === 'absent') absent += 1;
    const ms = approved(d);
    // Trạng thái ngày đã duyệt mà chưa có tiền theo ngày → vẫn ghi để NV đối chiếu
    for (const m of ms) {
      const k: PayslipExtraKey | null =
        m.kind === 'overnight' ? 'overnight' : m.kind === 'late_return' ? 'lateReturn' : m.kind === 'support' ? 'support' : null;
      if (k && !dayset[k].has(d)) {
        dayset[k].add(d);
        // Anh Tiến 05/11/2026 "qua đêm đã duyệt sao ko có tiền" → ghi rõ lý do thay cho câu chung
        const reason = k === 'support' ? supportNoteOf.get(d) : k === 'overnight' || k === 'lateReturn' ? nightNoteOf.get(d) : undefined;
        const why = reason
          ? `đã duyệt, không có tiền ${k === 'support' ? 'hỗ trợ' : 'đêm'}: ${reason}`
          : paidDays.has(d)
            ? 'đã duyệt, tiền ngày này ghi ở mục khác'
            : 'đã duyệt, ngày này không phát sinh tiền';
        extras[k].notes.push(`${ddmm(d)} ${PAYSLIP_EXTRA_LABELS[k]}${m.place && m.place.replace(/[.\s-]/g, '') ? ` (${m.place})` : ''}: ${why}`);
      }
    }
    if (!isDriver && c.otHours > 0) {
      otHours += c.otHours;
      dayset.ot.add(d);
      extras.ot.notes.push(`${ddmm(d)}: +${r2(c.otHours).toLocaleString('vi-VN')}h`);
    }
    days.push({ d, st, sym: myAttendanceCellText(c, d, i.today), ot: r2(c.otHours), marks: ms.map((m) => m.kind), ...(paidDays.has(d) ? { paid: true } : {}) });
  }
  for (const k of PAYSLIP_EXTRA_KEYS) {
    extras[k].qty = k === 'ot' && !isDriver ? r2(otHours) : dayset[k].size;
    if (extras[k].amount !== null) extras[k].amount = Math.round(extras[k].amount!);
  }
  // Ghi chú theo thứ tự ngày (dòng tổng không có ngày để cuối)
  for (const k of PAYSLIP_EXTRA_KEYS) {
    extras[k].notes.sort((a, b) => {
      const ka = /^\d\d\/\d\d/.test(a) ? a.slice(3, 5) + a.slice(0, 2) : '99';
      const kb = /^\d\d\/\d\d/.test(b) ? b.slice(3, 5) + b.slice(0, 2) : '99';
      return ka.localeCompare(kb);
    });
  }
  return {
    v: 1,
    month: i.month,
    std: i.std,
    work: r2(i.totals.work),
    p: r2(i.totals.leavePaid ?? p),
    kl: r2(i.totals.leaveUnpaid ?? kl),
    absent,
    lcn,
    late: i.totals.lateCount,
    days,
    extras,
    leave: i.leave ?? null,
  };
}

/** Phép năm tháng này từ 1 dòng sổ phép (leaveLedger): +1 hoặc 0 kèm lý do. */
export function payslipLeaveOf(
  row:
    | { eligible: boolean; work: number; accrued: number; forfeit: 'absent' | 'unexcused' | null; closing: number; unexcused?: number; absent?: number }
    | null
    | undefined,
  s: { leaveAccrualMinWork: number; leaveForfeitAbsentDays?: number; leaveForfeitUnexcusedDays?: number },
): PayslipLeave | null {
  if (!row) return null;
  if (row.accrued > 0) return { accrued: row.accrued, reason: null, detail: null, closing: row.closing };
  if (!row.eligible) return { accrued: 0, reason: 'not_official', detail: null, closing: row.closing };
  if (row.work < s.leaveAccrualMinWork) {
    return { accrued: 0, reason: 'low_work', detail: `công ${r2(row.work).toLocaleString('vi-VN')} < ${s.leaveAccrualMinWork}`, closing: row.closing };
  }
  if (row.forfeit === 'unexcused') {
    return {
      accrued: 0,
      reason: 'unexcused',
      detail: `vắng không phép ${row.unexcused ?? 0} ngày (từ ${s.leaveForfeitUnexcusedDays ?? 0} ngày mất phép)`,
      closing: row.closing,
    };
  }
  if (row.forfeit === 'absent') {
    const n = (row.unexcused ?? 0) + (row.absent ?? 0);
    return {
      accrued: 0,
      reason: 'absent',
      detail: `nghỉ ${r2(n).toLocaleString('vi-VN')} ngày (từ ${s.leaveForfeitAbsentDays ?? 0} ngày mất phép)`,
      closing: row.closing,
    };
  }
  return { accrued: 0, reason: null, detail: null, closing: row.closing };
}

/**
 * Anh Tiến 04/11/2026 (bảng lương): "Sau Công, thêm cột Phép/KL/V. VD Nguyễn Văn Đủ: 24 công, 3 Phép".
 * Công = ngày đi làm thật (công − phép có lương); cột Phép/KL/V ghi "3 P", "1 KL · 2 V". Dòng chưa có tờ công: giữ công cũ, cột trống.
 */
export function payrollLineDays(l: { workDays: number; detail?: { sheet?: Pick<PayslipSheet, 'p' | 'kl' | 'absent'> | null } | null }): { worked: number; off: string } {
  const sh = l.detail?.sheet;
  if (!sh) return { worked: l.workDays, off: '' };
  const fmt = (n: number) => r2(n).toLocaleString('vi-VN');
  const off = [sh.p > 0 && `${fmt(sh.p)} P`, sh.kl > 0 && `${fmt(sh.kl)} KL`, sh.absent > 0 && `${fmt(sh.absent)} V`].filter(Boolean).join(' · ');
  return { worked: Math.max(0, r2(l.workDays - sh.p)), off };
}
